import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Eye, Send, Mail, Package, Users, RefreshCw, Upload, Palette } from 'lucide-react';
import api, { inventoryAPI, clientsAPI, settingsAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EmailTemplates() {
  const [templateType, setTemplateType] = useState('single');
  const [config, setConfig] = useState({
    single: { headline: 'Check Out This Unit!', subheadline: 'Canadian Truck Sales has the equipment you need.', cta_text: 'View Full Details', cta_color: '#E31937', bg_color: '#E31937', footer_text: 'Canadian Truck Sales | 5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2 | (416) 417-2525', logo_url: '' },
    multi: { headline: 'New Equipment Available!', subheadline: "Canadian Truck Sales just added new units to our inventory. Don't miss out!", cta_text: 'View Details', cta_color: '#E31937', bg_color: '#E31937', footer_text: 'Canadian Truck Sales | 5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2 | (416) 417-2525', logo_url: '' },
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get('/email-templates/config').then(r => {
      const d = r.data;
      setConfig(c => ({
        single: { ...c.single, ...d.single },
        multi: { ...c.multi, ...d.multi },
      }));
    }).catch(() => {});
    inventoryAPI.list({ limit: 50 }).then(r => setInventory(r.data?.items || [])).catch(() => {});
    clientsAPI.list().then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  const currentConfig = config[templateType];
  const updateField = (field, value) => {
    setConfig(c => ({ ...c, [templateType]: { ...c[templateType], [field]: value } }));
  };

  const generatePreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/email-templates/preview', {
        type: templateType,
        item_ids: selectedItems.length > 0 ? selectedItems : [],
        config: currentConfig,
        base_url: API_URL,
      });
      setPreviewHtml(data.html);
    } catch { toast.error('Failed to generate preview'); }
    setLoading(false);
  }, [templateType, selectedItems, currentConfig]);

  useEffect(() => { generatePreview(); }, [generatePreview]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/email-templates/config', config);
      toast.success('Template saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleSend = async () => {
    if (selectedItems.length === 0) { toast.error('Select at least one item'); return; }
    if (selectedRecipients.length === 0) { toast.error('Select at least one recipient'); return; }
    setSending(true);
    try {
      const { data } = await api.post('/email-templates/send', {
        type: templateType,
        item_ids: selectedItems,
        recipient_ids: selectedRecipients,
        config: currentConfig,
        base_url: API_URL,
      });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const { data } = await settingsAPI.uploadLogo('email', file);
      updateField('logo_url', data.url);
      toast.success('Email logo uploaded');
    } catch { toast.error('Failed to upload logo'); }
    setUploadingLogo(false);
  };

  const ColorPicker = ({ value, onChange, label, testId }) => (
    <div>
      <label className="text-[10px] text-gray-400 uppercase">{label}</label>
      <div className="flex items-center gap-2 mt-0.5">
        <label className="relative w-10 h-10 rounded-lg border-2 border-gray-300 cursor-pointer flex-shrink-0 overflow-hidden hover:border-gray-400 transition-colors" style={{ backgroundColor: value }} data-testid={testId}>
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 w-[200%] h-[200%] cursor-pointer" style={{ opacity: 0, left: '-50%', top: '-50%' }} />
        </label>
        <Input value={value} onChange={e => onChange(e.target.value)} className="bg-gray-50 text-sm flex-1 h-10" />
      </div>
    </div>
  );

  return (
    <div data-testid="email-templates-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-sm text-gray-500">Design and send professional equipment emails</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-gray-300" onClick={generatePreview} disabled={loading} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-1" /> Preview
          </Button>
          <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left - Editor */}
        <div className="space-y-4">
          {/* Template Type */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 font-semibold uppercase mb-2 flex items-center gap-1"><Mail className="w-3 h-3 text-[#E31937]" /> Template Type</p>
              <div className="flex gap-2">
                {[{ key: 'single', label: 'Single', icon: Package }, { key: 'multi', label: 'Multiple', icon: Users }].map(t => (
                  <Button key={t.key} variant={templateType === t.key ? 'default' : 'outline'} size="sm" onClick={() => setTemplateType(t.key)}
                    className={templateType === t.key ? 'bg-[#E31937] text-white' : 'border-gray-300'}>
                    <t.icon className="w-3 h-3 mr-1" /> {t.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Content Editor */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-400 uppercase">Headline</label>
                <Input value={currentConfig.headline} onChange={e => updateField('headline', e.target.value)} className="bg-gray-50 text-sm mt-0.5" data-testid="email-headline" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase">Subheadline</label>
                <Input value={currentConfig.subheadline} onChange={e => updateField('subheadline', e.target.value)} className="bg-gray-50 text-sm mt-0.5" data-testid="email-subheadline" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">CTA Button Text</label>
                  <Input value={currentConfig.cta_text} onChange={e => updateField('cta_text', e.target.value)} className="bg-gray-50 text-sm mt-0.5" data-testid="email-cta-text" />
                </div>
                <ColorPicker value={currentConfig.cta_color} onChange={v => updateField('cta_color', v)} label="CTA Color" testId="email-cta-color" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase">Footer Text</label>
                <Input value={currentConfig.footer_text} onChange={e => updateField('footer_text', e.target.value)} className="bg-gray-50 text-sm mt-0.5" data-testid="email-footer" />
              </div>
            </CardContent>
          </Card>

          {/* Design Options */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1"><Palette className="w-4 h-4 text-[#E31937]" /> Design Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ColorPicker value={currentConfig.bg_color || '#E31937'} onChange={v => updateField('bg_color', v)} label="Header / Accent Background Color" testId="email-bg-color" />
              <div>
                <label className="text-[10px] text-gray-400 uppercase">Email Logo</label>
                <div className="flex items-center gap-3 mt-1">
                  {currentConfig.logo_url ? (
                    <div className="w-20 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden">
                      <img src={currentConfig.logo_url.startsWith('/api/') ? `${API_URL}${currentConfig.logo_url}` : currentConfig.logo_url} alt="Email Logo" className="max-h-10 max-w-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-20 h-12 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-300 text-[10px]">No logo</div>
                  )}
                  <div>
                    <Button size="sm" variant="outline" className="border-gray-300 text-xs" onClick={() => document.getElementById('email-logo-input').click()} disabled={uploadingLogo} data-testid="upload-email-logo">
                      <Upload className="w-3 h-3 mr-1" /> {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                    <input id="email-logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    {currentConfig.logo_url && (
                      <Button size="sm" variant="ghost" className="text-xs text-gray-400 ml-1" onClick={() => updateField('logo_url', '')}>Clear</Button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">If not set, the primary branding logo will be used</p>
              </div>
            </CardContent>
          </Card>

          {/* Select Equipment */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2"><Package className="w-4 h-4 text-[#E31937]" /> Select Equipment ({selectedItems.length})</span>
                <Button size="sm" variant="ghost" className="text-xs text-[#E31937] h-6 px-2" data-testid="select-all-equipment"
                  onClick={() => setSelectedItems(s => s.length === inventory.length ? [] : inventory.map(i => i.id))}>
                  {selectedItems.length === inventory.length ? 'Deselect All' : 'Select All'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {inventory.map(item => (
                  <label key={item.id} className="flex items-center gap-2 text-xs text-gray-700 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={e => {
                      setSelectedItems(s => e.target.checked ? [...s, item.id] : s.filter(i => i !== item.id));
                    }} className="accent-[#E31937]" />
                    {item.title || `${item.year} ${item.make} ${item.model}`}
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recipients + Send */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#E31937]" /> Recipients ({selectedRecipients.length})</span>
                <Button size="sm" variant="ghost" className="text-xs text-[#E31937] h-6 px-2" data-testid="select-all-recipients"
                  onClick={() => setSelectedRecipients(s => s.length === clients.length ? [] : clients.map(c => c.id))}>
                  {selectedRecipients.length === clients.length ? 'Deselect All' : 'Select All'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {clients.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-xs text-gray-700 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={selectedRecipients.includes(c.id)} onChange={e => {
                      setSelectedRecipients(s => e.target.checked ? [...s, c.id] : s.filter(i => i !== c.id));
                    }} className="accent-[#E31937]" />
                    {c.name} {c.email ? `(${c.email})` : ''}
                  </label>
                ))}
              </div>
              <Button className="w-full mt-3 bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSend} disabled={sending || selectedItems.length === 0 || selectedRecipients.length === 0} data-testid="send-email-btn">
                <Send className="w-3 h-3 mr-1" /> {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right - Live Preview */}
        <div>
          <Card className="bg-white border-gray-200 sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-green-500" /> Live Preview</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="h-64 flex items-center justify-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin" /></div>
              ) : previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full border-0 rounded-b-lg"
                  style={{ minHeight: '500px' }}
                  title="Email Preview"
                  sandbox="allow-same-origin"
                  data-testid="email-preview-iframe"
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Click Preview to see template</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
